import cx from 'classnames'
import dayjs from 'dayjs'
import { Fragment, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Gift } from 'lib/_types/gift'
import { GiftExchangeHistoryItem } from 'lib/_types'
import { useModal } from 'store/slices/modals'
import { useGetGiftExchangeTransactionDetailsQuery } from 'store/api/yay/gifts'

import ImageLoader from 'components/ImageLoader'

export const useGiftExchangeTransactionModal = () =>
  useModal<GiftExchangeHistoryItem>('GiftExchangeTransactionModal')

const ExchangeTransactionDetails = ({
  transaction,
}: {
  transaction: Partial<GiftExchangeHistoryItem>
}) => {
  const { t } = useTranslation()
  const getTransactionDetailsRes = useGetGiftExchangeTransactionDetailsQuery({
    uuid: transaction.uuid,
  })

  const { displayAmount, displayStatus } = useMemo(() => {
    let displayAmount = '--'
    let displayStatus = '--'

    if (getTransactionDetailsRes.isLoading) {
      displayAmount = t('common.loading')
      displayStatus = t('common.loading')
    } else if (
      getTransactionDetailsRes.isSuccess &&
      getTransactionDetailsRes.data
    ) {
      displayAmount = `${Number(
        getTransactionDetailsRes?.data?.rewarded_amount
      ).toLocaleString()}`
      displayStatus = getTransactionDetailsRes?.data?.status
    }

    return { displayAmount, displayStatus }
  }, [getTransactionDetailsRes])

  return (
    <Fragment>
      <div className="TransactionDetails__summary">
        <div className="TransactionDetails__summary__heading">
          {t('empl.exchange')}
        </div>
        <div className="TransactionDetails__summary__image TransactionDetails__summary__image--empl">
          <ImageLoader url="/images/icon-empl-coin.svg" />
        </div>
        <div className="TransactionDetails__summary__emplAmount TransactionDetails__summary__emplAmount--show-currency">
          {displayAmount}
        </div>
      </div>
      <div className="TransactionDetails__dl">
        <div className="TransactionDetails__detailRow">
          <div className="TransactionDetails__dt">
            {t('empl.transaction_details.status')}
          </div>
          <div className="TransactionDetails__dd">
            <span
              className={cx('TransactionDetails__dd', {
                'TransactionDetails__dd--success': displayStatus === 'success',
              })}
            >
              {displayStatus}
            </span>
            <br />
            <time
              className="PostTime"
              title={dayjs(transaction.created_at * 1000).format()}
            >
              {dayjs(transaction.created_at * 1000).format(
                'YYYY/MM/DD HH:mm:ss'
              )}
            </time>
          </div>
        </div>
      </div>
      <div className="TransactionDetails__dl">
        {getTransactionDetailsRes.data?.items?.map(
          (item: { quantity: number; gift: Gift }, i: number) => {
            return (
              <div className="TransactionDetails__detailRow" key={`gift_${i}`}>
                <div className="TransactionDetails__dt">
                  <div className="GiftLabel">
                    <ImageLoader
                      className="GiftLabel__icon"
                      url={item.gift?.icon_thumbnail}
                    />
                    <div className="GiftLabel__title">{item.gift?.title}</div>
                  </div>
                </div>
                <div className="TransactionDetails__dd--black">
                  {item.quantity}
                </div>
              </div>
            )
          }
        )}
      </div>
    </Fragment>
  )
}

const GiftExchangeTransactionModal = () => {
  const { t } = useTranslation()
  const modal = useGiftExchangeTransactionModal()

  const onClose = modal.onClose

  return (
    <div
      className={cx('Modal GiftExchangeTransactionModal', {
        'Modal--active': modal.isActive,
      })}
    >
      <a className="Modal__overlay" onClick={() => onClose()} />
      {modal.isActive && (
        <div className="Modal__wrapper Modal__wrapper--auto-h">
          <div className="Modal__header Modal__header--bdrz-t">
            <a className="Modal__header__close" onClick={() => onClose()} />

            <h2 className="Modal__header__h2">
              {t('empl.transaction_details')}
            </h2>
          </div>
          <div className="Modal__body">
            <div className="TransactionDetails__wrapper">
              <ExchangeTransactionDetails transaction={modal.data} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default GiftExchangeTransactionModal
